import React from 'react';
import { DNAChip, DNAChipProps as LuxDNAChipProps } from '@alucio/lux-ui';
import { useAppSettings } from 'src/state/context/AppSettings';
import { getVariantProps, DNAChipBeaconVariant } from './variants';

export interface DNAChipProps extends LuxDNAChipProps {
  variant?: DNAChipBeaconVariant;
}

/**
 * DNAChip component with Beacon-specific variants and types.
 *
 * @example
 * // Basic usage with string children
 * <DNAChip.Beacon variant={DNAChip.Variant.USER_FILTER}>My Filter</DNAChip.Beacon>
 *
 * // Usage without variant (uses original DNAChip props)
 * <DNAChip.Beacon>Regular children</DNAChip.Beacon>
 */
interface DNAChipComponent extends React.FC<LuxDNAChipProps> {
  /**
   * Beacon-specific DNAChip component with variant support.
   * The chip size is automatically set based on device mode.
   */
  Beacon: React.FC<DNAChipProps>;

  /**
   * Constant object containing all available variant values.
   * Use this to ensure type safety when specifying variants.
   *
   * @example
   * // Correct usage with type safety
   * <DNAChip.Beacon variant={DNAChip.Variant.REQUIRED} />
   * <DNAChip.Beacon variant="REQUIRED" />
   *
   * // TypeScript will error if you use an invalid variant
   * <DNAChip.Beacon variant="INVALID_VARIANT" /> // Error
   */
  Variant: typeof DNAChipBeaconVariant;
}

/**
 * Beacon-specific DNAChip component that supports variants and change size based on device mode.
 */
export const DNAChipBeacon: React.FC<DNAChipProps> = ({ variant, children, ...props }) => {
  const { deviceMode } = useAppSettings();
  const variantProps = getVariantProps(variant, deviceMode, children);
  return <DNAChip {...variantProps} {...props} />;
};

const DNAChipWithBeacon = Object.assign(DNAChip, {
  Beacon: DNAChipBeacon,
  Variant: DNAChipBeaconVariant,
}) as DNAChipComponent;

export default DNAChipWithBeacon;
